import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, Loader2, MessageCircle, ShieldX, FileText } from "lucide-react";
import {
  listBousalaPurchasesAdmin,
  approveBousalaPurchase,
  rejectBousalaPurchase,
  getBousalaReceiptSignedUrl,
  resendBousalaWhatsApp,
} from "@/lib/bousala-purchase.functions";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

type ProductKey = "v1" | "smart";

const PRODUCT_TABS: Array<{ key: ProductKey; label: string }> = [
  { key: "v1", label: "بوصلة د. سارة" },
  { key: "smart", label: "البوصلة الذكية" },
];

const listQ = (product: ProductKey) =>
  queryOptions({
    queryKey: ["admin", "bousala", "purchases", product],
    queryFn: () => listBousalaPurchasesAdmin({ data: { product } }),
  });

export const Route = createFileRoute("/_authenticated/admin/bousala/purchases")({
  loader: ({ context }) => context.queryClient.ensureQueryData(listQ("v1")),
  head: () => ({
    meta: [{ title: "مدفوعات البوصلة — إدارة" }, { name: "robots", content: "noindex" }],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: PurchasesAdmin,
});

function statusBadge(status: string) {
  const map: Record<string, string> = {
    pending: "bg-amber-100 text-amber-900",
    paid: "bg-emerald-100 text-emerald-900",
    granted: "bg-emerald-100 text-emerald-900",
    rejected: "bg-rose-100 text-rose-900",
    refunded: "bg-slate-100 text-slate-900",
  };
  const label: Record<string, string> = {
    pending: "قيد المراجعة",
    paid: "مدفوع",
    granted: "مفعّل",
    rejected: "مرفوض",
    refunded: "مسترجع",
  };
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${map[status] ?? "bg-slate-100 text-slate-900"}`}>
      {label[status] ?? status}
    </span>
  );
}

function PurchasesAdmin() {
  const [product, setProduct] = useState<ProductKey>("v1");
  const { data } = useSuspenseQuery(listQ(product));
  return (
    <div>
      <header className="mb-6">
        <p className="text-xs font-bold tracking-widest text-primary">إدارة</p>
        <h1 className="mt-1 font-display text-3xl font-extrabold text-foreground">مدفوعات البوصلة</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          راجع طلبات التحويل الداخلي وأرسل رابط التفعيل عبر واتساب بعد الموافقة.
        </p>
      </header>

      <div className="mb-6 inline-flex rounded-full bg-background p-1">
        {PRODUCT_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setProduct(tab.key)}
            aria-pressed={product === tab.key}
            className={`rounded-full px-5 py-2 text-xs font-bold transition ${
              product === tab.key
                ? "bg-primary text-primary-foreground shadow-soft"
                : "text-muted-foreground hover:bg-accent/30"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {data.length === 0 ? (
        <div className="rounded-ds-lg bg-card p-10 text-center shadow-soft">
          <p className="text-sm text-muted-foreground">لا توجد مدفوعات بعد.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {data.map((p: any) => (
            <PurchaseRow key={p.id} p={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function PurchaseRow({ p }: { p: any }) {
  const qc = useQueryClient();
  const approve = useServerFn(approveBousalaPurchase);
  const reject = useServerFn(rejectBousalaPurchase);
  const getReceipt = useServerFn(getBousalaReceiptSignedUrl);
  const resend = useServerFn(resendBousalaWhatsApp);
  const [busy, setBusy] = useState<"approve" | "reject" | "receipt" | "resend" | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  const methodLabel =
    p.payment_method === "paypal"
      ? "PayPal"
      : p.payment_method === "bank_transfer_sy"
        ? "تحويل داخلي (سوريا)"
        : "بطاقة (سجل سابق)";
  const isPending = p.status === "pending";
  const isPendingKind = p.kind === "pending";

  async function openReceipt() {
    if (!p.receipt_file || busy) return;
    setBusy("receipt");
    try {
      const { url } = await getReceipt({ data: { path: p.receipt_file } });
      window.open(url, "_blank", "noopener");
    } catch (e: any) {
      toast.error(e?.message ?? "تعذّر فتح الإيصال.");
    } finally {
      setBusy(null);
    }
  }

  async function doApprove() {
    if (busy) return;
    setBusy("approve");
    try {
      const payload = isPendingKind ? { pendingId: p.id } : { purchaseId: p.id };
      const res: any = await approve({ data: payload });
      const { waLink, magicLink } = res;
      if (res.existingAccount) {
        if (waLink) window.open(waLink, "_blank", "noopener");
        toast.warning(`تم التفعيل. ${res.notice}`, { duration: 10000 });
      } else if (waLink) {
        window.open(waLink, "_blank", "noopener");
        toast.success("تم التفعيل — افتح محادثة واتساب لإرسال الرابط.");
      } else {
        // no phone; copy link
        try {
          await navigator.clipboard.writeText(magicLink);
          toast.success("تم التفعيل — نسخنا رابط الدخول (لا يوجد رقم واتساب).");
        } catch {
          toast.success("تم التفعيل. الرابط: " + magicLink);
        }
      }
      qc.invalidateQueries({ queryKey: ["admin", "bousala", "purchases"] });
    } catch (e: any) {
      toast.error(e?.message ?? "فشل التفعيل.");
    } finally {
      setBusy(null);
    }
  }

  async function doReject() {
    if (busy || rejectReason.trim().length < 2) return;
    setBusy("reject");
    try {
      const payload = isPendingKind
        ? { pendingId: p.id, reason: rejectReason.trim() }
        : { purchaseId: p.id, reason: rejectReason.trim() };
      const { waLink } = await reject({ data: payload });
      if (waLink) {
        window.open(waLink, "_blank", "noopener");
        toast.success("تم رفض الطلب — افتح واتساب لإبلاغ العميلة.");
      } else {
        toast.success("تم رفض الطلب.");
      }
      setShowReject(false);
      setRejectReason("");
      qc.invalidateQueries({ queryKey: ["admin", "bousala", "purchases"] });
    } catch (e: any) {
      toast.error(e?.message ?? "فشل الرفض.");
    } finally {
      setBusy(null);
    }
  }

  async function doResend() {
    if (busy) return;
    setBusy("resend");
    try {
      const { waLink, magicLink } = await resend({ data: { purchaseId: p.id } });
      if (waLink) {
        window.open(waLink, "_blank", "noopener");
        toast.success("تم إنشاء رابط جديد — افتح واتساب لإرساله.");
      } else {
        try {
          await navigator.clipboard.writeText(magicLink);
          toast.success("نسخنا رابط الدخول (لا يوجد رقم واتساب).");
        } catch {
          toast.success("الرابط: " + magicLink);
        }
      }
    } catch (e: any) {
      toast.error(e?.message ?? "تعذّر إعادة الإرسال.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="rounded-ds-lg bg-card p-5 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="font-display text-base font-extrabold text-foreground">
              {p.profile?.full_name || "—"}
            </h3>
            {statusBadge(p.status)}
            <span className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
              {methodLabel}
            </span>
            {p.profile?.country ? (
              <span className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
                {p.profile.country}
              </span>
            ) : null}
            <span className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
              {p.environment === "live" ? "live" : "test"}
            </span>
            {p.product_title ? (
              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                {p.product_title}
              </span>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span>{p.profile?.email || "—"}</span>
            <span>{p.profile?.phone_whatsapp || "—"}</span>
            <span>
              {p.amount_cents != null ? `$${(p.amount_cents / 100).toFixed(2)} ${p.currency ?? "USD"}` : "—"}
            </span>
            <span>{new Date(p.created_at).toLocaleString("ar-u-nu-latn")}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {p.receipt_file ? (
            <button
              type="button"
              onClick={openReceipt}
              disabled={busy === "receipt"}
              className="inline-flex items-center gap-1.5 rounded-full bg-background px-4 py-2 text-xs font-bold text-foreground hover:bg-accent/30 disabled:opacity-50"
            >
              {busy === "receipt" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
              الإيصال
            </button>
          ) : null}
          {isPending && (
            <>
              <button
                type="button"
                onClick={doApprove}
                disabled={!!busy}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {busy === "approve" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageCircle className="h-3.5 w-3.5" />}
                تفعيل + واتساب
              </button>
              <button
                type="button"
                onClick={() => setShowReject((s) => !s)}
                disabled={!!busy}
                className="inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-transparent px-4 py-2 text-xs font-bold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              >
                <ShieldX className="h-3.5 w-3.5" /> رفض
              </button>
            </>
          )}
          {(p.status === "paid" || p.status === "granted") && (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-4 py-2 text-xs font-bold text-emerald-900">
                <CheckCircle2 className="h-3.5 w-3.5" /> تم التفعيل
              </span>
              <button
                type="button"
                onClick={doResend}
                disabled={!!busy}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {busy === "resend" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageCircle className="h-3.5 w-3.5" />}
                إعادة الإرسال عبر واتساب
              </button>
            </>
          )}
        </div>
      </div>

      {showReject && (
        <div className="mt-4 rounded-2xl bg-background/70 p-4">
          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-foreground">سبب الرفض (يُحفظ داخليًا)</span>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowReject(false)}
              className="rounded-full bg-background px-4 py-2 text-xs font-bold text-muted-foreground"
            >
              إلغاء
            </button>
            <button
              type="button"
              onClick={doReject}
              disabled={busy === "reject" || rejectReason.trim().length < 2}
              className="inline-flex items-center gap-1.5 rounded-full bg-destructive px-4 py-2 text-xs font-bold text-destructive-foreground shadow-soft hover:bg-destructive/90 disabled:opacity-50"
            >
              {busy === "reject" && <Loader2 className="h-3.5 w-3.5 animate-spin" />} تأكيد الرفض
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
